import { QuestionOutlined } from '@ant-design/icons';
import { Avatar, Popover, notification } from 'antd';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getRandomColor } from '../../../../../../js/helper';
import { CHART_COLORS } from '../constants';

const USER_COLORS_SET = new Map();

const UNASSIGNED_COLOR = CHART_COLORS.LABELS;
const UNASSIGNED_ICON_COLOR = CHART_COLORS.LIGHT_LABELS;
const UNASSIGNED_TEXT = 'Unassigned';

const AVATAR_SIZE = 24;
const AVATAR_BORDER_STYLE = `2px solid ${CHART_COLORS.WHITE}`;

const AVATAR_STYLE = {
  border: AVATAR_BORDER_STYLE,
};

const UNASSIGNED_AVATAR_STYLE = {
  backgroundColor: UNASSIGNED_COLOR,
  color: CHART_COLORS.WHITE,
  fontSize: 12,
  fontWeight: 600,
  border: AVATAR_BORDER_STYLE,
};

const INITIALS_AVATAR_STYLE = {
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  borderRadius: '50%',
  color: CHART_COLORS.WHITE,
  fontSize: 12,
  fontWeight: 600,
  border: AVATAR_BORDER_STYLE,
};

const POPOVER_CARD_STYLE = {
  width: '26em',
  height: '9rem',
  borderRadius: '8px',
  overflow: 'hidden',
};

const AVATAR_SECTION_STYLE = {
  objectFit: 'cover',
};

const POPOVER_CONFIG = {
  trigger: 'hover',
  placement: 'top',
  arrow: false,
  overlayStyle: { zIndex: 1000 },
  mouseEnterDelay: 0.1,
  mouseLeaveDelay: 0.2,
};

const UserAvatar = ({
  isDefaultAvatar,
  userColor,
  userInitials,
  avi,
  fullName,
}) => {
  if (isDefaultAvatar) {
    return <div className="text-white text-5xl font-bold">{userInitials}</div>;
  }

  return (
    <img
      src={avi}
      alt={fullName}
      className="h-full w-full"
      style={AVATAR_SECTION_STYLE}
      referrerPolicy="no-referrer"
    />
  );
};

const UnassignedAvatar = () => (
  <div className="flex items-center justify-center h-full">
    <QuestionOutlined style={{ fontSize: 48, color: UNASSIGNED_ICON_COLOR }} />
  </div>
);

const ActionButton = ({ icon, messageId, defaultMessage, onClick }) => (
  <div
    className="flex items-center p-1 hover:bg-slate-100 rounded cursor-pointer px-3"
    onClick={onClick}
  >
    <img
      src={icon}
      alt={defaultMessage}
      className="h-4 w-4 mr-1"
      style={AVATAR_SECTION_STYLE}
    />
    <p className="text-xs">
      <FormattedMessage id={messageId} defaultMessage={defaultMessage} />
    </p>
  </div>
);

const UserPopoverContent = ({
  user,
  userColor,
  isDefaultAvatar,
  userInitials,
  isUnassigned,
  onCopyEmail,
}) => (
  <div className="flex bg-white" style={POPOVER_CARD_STYLE}>
    <div
      className="h-full w-[40%] cursor-pointer flex items-center justify-center"
      style={{ backgroundColor: isUnassigned ? UNASSIGNED_COLOR : userColor }}
    >
      {isUnassigned ? (
        <UnassignedAvatar />
      ) : (
        <UserAvatar
          isDefaultAvatar={isDefaultAvatar}
          userColor={userColor}
          userInitials={userInitials}
          avi={user?.avi}
          fullName={user?.fullName}
        />
      )}
    </div>

    <div className="h-full flex flex-col justify-center w-[60%] px-4 py-5">
      <p className="font-bold text-md" style={{ color: CHART_COLORS.DARK_LABELS }}>
        {isUnassigned ? UNASSIGNED_TEXT : user?.fullName}
      </p>
      <p
        className="font-light text-xs mb-2"
        style={{
          color: CHART_COLORS.LABELS,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {isUnassigned ? '—' : user?.email || '—'}
      </p>

      {!isUnassigned && (
        <div className="flex items-center gap-2 mt-auto">
          <ActionButton
            icon="/images/icons/copy.svg"
            messageId="COPY_EMAIL"
            defaultMessage="copy email"
            onClick={onCopyEmail}
          />
        </div>
      )}
    </div>
  </div>
);

const AvatarTick = ({ x, y, payload, user }) => {
  const intl = useIntl();

  // Check if unassigned
  const isUnassigned =
    !user ||
    !payload?.value ||
    payload.value.toLowerCase() === 'unassigned' ||
    payload.value.toLowerCase() === 'none' ||
    payload.value.toLowerCase() === 'n/a';

  // Get user initials
  const userInitials = useMemo(() => {
    if (isUnassigned || !user?.fullName) return null;

    const splittedName = user.fullName.split(' ');
    const result =
      splittedName.length > 1
        ? `${splittedName[0].slice(0, 1)}${splittedName[splittedName.length - 1].slice(0, 1)}`
        : splittedName[0].slice(0, 1);
    return result.toUpperCase();
  }, [user?.fullName, isUnassigned]);

  // Get user color
  const userColor = useMemo(() => {
    if (isUnassigned || !userInitials) {
      return UNASSIGNED_COLOR;
    }

    const first = userInitials.charCodeAt(0);
    const second = userInitials.charCodeAt(1) || 0;
    const asciiInitial = first + second;

    const colorFromMap = USER_COLORS_SET.get(user._id);
    if (colorFromMap) {
      return colorFromMap;
    }

    const color = getRandomColor(asciiInitial);
    USER_COLORS_SET.set(user._id, color);
    return color;
  }, [user?._id, userInitials, isUnassigned]);

  // Check if default avatar
  const isDefaultAvatar = useMemo(
    () => !user?.avi || user.avi.includes('pmc/public/users/global/no_avi.png'),
    [user?.avi],
  );

  const handleCopyEmail = () => {
    if (isUnassigned || !user?.email) return;

    navigator.clipboard.writeText(user.email);
    notification.success({
      message: intl.formatMessage({
        id: 'EMAIL_COPIED_TO_CLIPBOARD',
        defaultMessage: 'Email copied to clipboard',
      }),
      placement: 'bottomRight',
    });
  };

  const popoverContent = (
    <UserPopoverContent
      user={user}
      userColor={userColor}
      isDefaultAvatar={isDefaultAvatar}
      userInitials={userInitials}
      isUnassigned={isUnassigned}
      onCopyEmail={handleCopyEmail}
    />
  );

  const avatarElement = (
    <foreignObject x={-12} y={2} width={AVATAR_SIZE} height={AVATAR_SIZE}>
      {isUnassigned ? (
        <Avatar
          size={AVATAR_SIZE}
          icon={<QuestionOutlined />}
          style={UNASSIGNED_AVATAR_STYLE}
        />
      ) : isDefaultAvatar ? (
        <div
          className="flex items-center justify-center"
          style={{
            ...INITIALS_AVATAR_STYLE,
            backgroundColor: userColor,
          }}
        >
          {userInitials}
        </div>
      ) : (
        <Avatar size={AVATAR_SIZE} src={user.avi} style={AVATAR_STYLE} />
      )}
    </foreignObject>
  );

  return (
    <Popover content={popoverContent} {...POPOVER_CONFIG}>
      <g transform={`translate(${x},${y})`} style={{ cursor: 'pointer' }}>
        {avatarElement}
      </g>
    </Popover>
  );
};

export default AvatarTick;
